import { Mail, Phone, MapPin, Globe2, Clock } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal, MaskLines } from "@/components/Reveal";
import { Breadcrumbs } from "@/components/Blocks";
import { InquiryForm } from "@/components/InquiryForm";
import { WhatsAppIcon } from "@/components/Icon";
import { EMAIL, PHONES, ADDRESS, WA_DEFAULT } from "@/data/site";

const Row = ({ icon: I, label, children }) => (
  <div className="flex gap-4 border-b border-stone py-5">
    <I className="mt-0.5 h-5 w-5 shrink-0 text-rust-deep" aria-hidden="true" />
    <div><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-graphite">{label}</p><div className="mt-1.5 text-[15px] text-charcoal">{children}</div></div>
  </div>
);

export default function Contact() {
  const crumbs = [["Home", "/"], ["Contact Us", "/contact"]];
  return (
    <>
      <Seo title="Contact Industrix Solution | Discuss Your Industrial Project" description="Contact Industrix Solution about websites, ERP, CRM, mobile apps, automation or SEO for your manufacturing business. WhatsApp, email or phone. Based in India, working globally." path="/contact" jsonLd={[breadcrumbSchema(crumbs)]} />
      <section className="relative overflow-hidden border-b border-stone" data-testid="contact-hero">
        <div className="grid-lines absolute inset-0" aria-hidden="true" />
        <div className="wrap relative pb-14 pt-12 lg:pb-20 lg:pt-16">
          <Breadcrumbs items={crumbs} />
          <p className="eyebrow">Contact us</p>
          <MaskLines lines={["Let's Discuss Your", "Industrial Project"]} className="mt-6 text-4xl font-extrabold leading-[1.02] sm:text-5xl lg:text-6xl" testId="page-title" />
          <Reveal delay={0.4}><p className="lead mt-8 max-w-2xl">Tell us about your business and what you would like to improve. We are based in Bhopal, India, and work with manufacturers and industrial businesses around the world.</p></Reveal>
        </div>
      </section>

      <section className="wrap grid gap-14 py-16 lg:grid-cols-[1.25fr_0.75fr] lg:py-24">
        <div id="inquiry" className="rounded-md border border-stone bg-white p-6 sm:p-10" data-testid="contact-form-card">
          <h2 className="font-display text-2xl font-bold">Project inquiry</h2>
          <p className="mt-2 text-[14px] text-graphite">Fields marked * are required. Send your enquiry and our team will reply.</p>
          <div className="mt-8"><InquiryForm /></div>
        </div>
        <aside className="space-y-10" data-testid="contact-details">
          <div>
            <h2 className="font-display text-2xl font-bold">Direct contact</h2>
            <div className="mt-4">
              <Row icon={WhatsAppIcon} label="WhatsApp"><a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="font-semibold text-rust-ink hover:underline" data-testid="contact-whatsapp-link">Chat on WhatsApp: +91 7024315567</a></Row>
              <Row icon={Phone} label="Phone">
                {PHONES.map((p) => <a key={p.href} href={p.href} className="block hover:text-rust-ink" data-testid={`contact-phone-${p.href.slice(-4)}`}>{p.label}</a>)}
              </Row>
              <Row icon={Mail} label="Email"><a href={`mailto:${EMAIL}`} className="break-all hover:text-rust-ink" data-testid="contact-email-link">{EMAIL}</a></Row>
              <Row icon={MapPin} label="Office"><address className="not-italic leading-relaxed" data-testid="contact-address">{ADDRESS.full}</address></Row>
              <Row icon={Globe2} label="Clients">Based in India (IST, UTC+5:30). Working with international clients by video call, email and WhatsApp.</Row>
            </div>
          </div>
          <div className="rounded-md bg-charcoal p-7 text-ivory">
            <Clock className="h-5 w-5 text-[#E8B795]" aria-hidden="true" />
            <h2 className="mt-4 font-display text-xl font-bold">How the inquiry process works</h2>
            <ol className="mt-5 space-y-3 text-[14px] leading-relaxed text-[#E4E0D6]">
              <li>1. Fill in the form with your name, email and phone number.</li>
              <li>2. Press send. Your enquiry is delivered to our team instantly.</li>
              <li>3. We review your requirements and reply with questions or next steps.</li>
              <li>4. If useful, we schedule a discovery call to understand your workflows.</li>
            </ol>
          </div>
        </aside>
      </section>
    </>
  );
}
